import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, ShieldCheck, Users, Award, ScrollText, Sprout } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/06a30dfa-b63f-4989-ad14-b93db4f46569/images/e5f095247e555e1aa365488f903cdad9076ab757829f984d7ca101de0eb212a0.png";
const HUMAN = "https://images.pexels.com/photos/5711163/pexels-photo-5711163.jpeg";
const CLINIC = "https://images.pexels.com/photos/7578810/pexels-photo-7578810.jpeg";
const STATE = "https://images.pexels.com/photos/5031655/pexels-photo-5031655.jpeg";

const DOMAINS = [
  { icon: Sprout, title: "Recovery & Wellness", text: "SAMHSA's ten guiding principles and the four dimensions, taught as practical conversation tools." },
  { icon: ShieldCheck, title: "Ethics & Boundaries", text: "The NC Code of Conduct, scope of practice, and the discipline of holding the helping frame." },
  { icon: Users, title: "Trauma-Informed Care", text: "ACEs, the four R's, and the science of not re-traumatizing the people who trust you." },
  { icon: BookOpen, title: "Crisis & Safety", text: "Columbia Protocol screening, Stanley-Brown safety planning, lethal-means counseling." },
  { icon: ScrollText, title: "Documentation & HIPAA", text: "Brief, behavioral, audit-defensible notes — and the special protections of 42 CFR Part 2." },
  { icon: Award, title: "Systems Navigation", text: "LME/MCOs, Tailored Plans, and how to advocate without burning the bridge you'll need next month." },
];

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-bone/60 via-bone/40 to-bone" aria-hidden />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12 pt-24 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="label-eyebrow mb-6" data-testid="hero-eyebrow">
              North Carolina · 40-Hour Certified Peer Support Specialist Training
            </div>
            <h1 className="font-serif text-[clamp(2.6rem,6vw,5.5rem)] leading-[0.95] tracking-tight text-ink">
              The clinical training
              <br />
              <span className="italic text-terracotta">peer support</span> deserves.
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-[58ch] leading-relaxed">
              A top-tier curriculum for North Carolina's Certified Peer Support Specialists — twelve
              modules, forty hours, written with the rigor of a clinical textbook and the warmth
              of the work itself.
            </p>
            <div className="mt-10 flex flex-wrap gap-4" data-testid="hero-cta-group">
              <Link to="/register" className="btn-primary" data-testid="hero-cta-enroll">
                Begin training <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
              <Link to="/curriculum-overview" className="btn-ghost" data-testid="hero-cta-curriculum">
                Read the curriculum
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="border-l border-stone pl-6 space-y-6">
              <div>
                <div className="font-serif text-5xl text-ink">40</div>
                <div className="label-eyebrow mt-1">Clinical Hours</div>
              </div>
              <div>
                <div className="font-serif text-5xl text-ink">12</div>
                <div className="label-eyebrow mt-1">Competency Modules</div>
              </div>
              <div>
                <div className="font-serif text-5xl text-ink">80<span className="text-2xl align-top">%</span></div>
                <div className="label-eyebrow mt-1">Mastery Threshold</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE / EDITORIAL */}
      <section className="border-y border-stone bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow mb-3">A note to the trainee</div>
            <h2 className="font-serif text-4xl text-ink leading-tight">
              Lived experience is the credential. This is the training that meets it.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 container-prose text-[1.05rem] leading-[1.85] text-foreground/90">
            <p className="drop-cap">
              The work you are about to learn is one of the few in modern behavioral health
              where what you have survived is the qualification. That gift carries a weight.
              The curriculum that follows treats your story with the seriousness it deserves —
              and asks you to extend the same seriousness to the people whose stories will
              soon be entrusted to you.
            </p>
            <p className="mt-6">
              We teach to the North Carolina standard — the framework administered by UNC's
              Behavioral Health Springboard, governed by 10A NCAC 27G, and informed by
              SAMHSA's recovery principles. We teach beyond it, too: where the field has
              moved, where the evidence has shifted, and where the discipline of presence
              actually lives.
            </p>
          </div>
        </div>
      </section>

      {/* DOMAINS GRID */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-24">
        <div className="editorial-rule mb-12" data-testid="domains-rule">Twelve modules · Six competency domains</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-stone">
          {DOMAINS.map((d, i) => (
            <div key={i} className="border-r border-b border-stone p-8 bg-white hover:bg-cream transition-colors group" data-testid={`domain-tile-${i}`}>
              <d.icon size={28} strokeWidth={1.4} className="text-terracotta mb-6 group-hover:text-ink transition-colors" />
              <h3 className="font-serif text-2xl text-ink mb-3">{d.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{d.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SPLIT IMAGE / TEXT */}
      <section className="bg-cream">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img src={HUMAN} alt="Peer support session" className="w-full h-[520px] object-cover" data-testid="img-community" />
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="label-eyebrow mb-4">Built for the field</div>
            <h2 className="font-serif text-5xl text-ink leading-tight mb-6">
              Written by people who do the work, for people about to do it.
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Every module is written in service of the conversations that actually happen — the
              2 a.m. text message, the group that goes sideways, the participant who tells you
              the thing they have told no one else. The curriculum is rigorous because the work
              is.
            </p>
            <Link to="/curriculum-overview" className="btn-ghost" data-testid="cta-read-curriculum">
              Read the full curriculum
            </Link>
          </div>
        </div>
      </section>

      {/* STANDARDS BAND */}
      <section className="border-y border-stone bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            ["10A NCAC 27G", "NC regulatory standard"],
            ["SAMHSA", "Recovery principles framework"],
            ["UNC BHS", "Certifying body"],
            ["42 CFR Part 2", "Confidentiality ceiling"],
          ].map(([t, s], i) => (
            <div key={i} className="border-l border-stone pl-6">
              <div className="font-serif text-2xl text-ink">{t}</div>
              <div className="label-eyebrow mt-2">{s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PARTNER BAND — Generational Health */}
      <section style={{ background: "#0B1F3A" }} className="text-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[0.72rem] tracking-[0.22em] uppercase mb-4" style={{ color: "#16A5BD" }}>
              Employment Partner · Generational Health
            </div>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.4rem)] leading-[1.05] mb-6">
              Train here.{" "}
              <span style={{ color: "#16A5BD" }} className="italic">Get hired there.</span>
            </h2>
            <p className="text-white/80 leading-relaxed max-w-[58ch]">
              Carolina Peer Institute graduates have a direct pipeline into Generational Health —
              a North Carolina behavioral health organization that hires Certified Peer Support
              Specialists, pays them as the professionals they are, and supports the long arc of
              the career.
            </p>
            <p className="mt-3 italic font-serif" style={{ color: "#16A5BD" }}>Your mind. Your power.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/generational-health"
                className="inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-[0.08em] uppercase font-medium transition-all"
                style={{ background: "#16A5BD", color: "#0B1F3A" }}
                data-testid="landing-gh-cta"
              >
                Explore careers <ArrowUpRight size={16} strokeWidth={1.8} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 flex items-center justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_peer-support-elite/artifacts/uxvvui3f_gh%20logo.png"
              alt="Generational Health"
              className="w-full max-w-[320px] aspect-square object-contain"
            />
          </div>
        </div>
      </section>

      {/* IMAGE FULL */}
      <section className="relative h-[480px] overflow-hidden">
        <img src={STATE} alt="North Carolina landscape" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/55" aria-hidden />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <div className="label-eyebrow text-bone/80 mb-4">For the state of North Carolina</div>
          <h2 className="font-serif text-bone text-[clamp(2.5rem,5vw,4.5rem)] max-w-[18ch] leading-[1.05]">
            Recovery happens in connection. Train accordingly.
          </h2>
          <Link to="/register" className="btn-primary mt-10 !bg-bone !text-ink hover:!bg-terracotta hover:!text-bone" data-testid="cta-bottom-enroll">
            Begin training
          </Link>
        </div>
      </section>
    </div>
  );
}
